import en from '@/locales/en.json';
import es from '@/locales/es.json';

export type Language = 'en' | 'es';

export const SUPPORTED_LANGUAGES: { value: Language; label: string; flag: string }[] = [
  { value: 'en', label: 'English', flag: '🇺🇸' },
  { value: 'es', label: 'Español', flag: '🇲🇽' },
];

const translations: Record<Language, typeof en> = { en, es };

type DeepValue<T> = T extends string ? string : T extends Record<string, unknown> ? { [K in keyof T]: DeepValue<T[K]> } : never;

function getNestedValue(obj: Record<string, unknown>, path: string): string | undefined {
  const keys = path.split('.');
  let current: unknown = obj;
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === 'string' ? current : undefined;
}

/**
 * Get a translation string by dot-notation key.
 * Supports simple {{variable}} interpolation.
 * Falls back to English if key not found in selected language.
 */
export function t(language: Language, key: string, vars?: Record<string, string | number>): string {
  const dict = translations[language] as unknown as Record<string, unknown>;
  let value = getNestedValue(dict, key);

  // Fallback to English
  if (value === undefined) {
    const enDict = translations['en'] as unknown as Record<string, unknown>;
    value = getNestedValue(enDict, key);
  }

  if (value === undefined) return key;

  // Interpolate variables
  if (vars) {
    return value.replace(/\{\{(\w+)\}\}/g, (_, varName) => {
      return vars[varName] !== undefined ? String(vars[varName]) : `{{${varName}}}`;
    });
  }

  return value;
}

/**
 * Detect browser language and return supported language code.
 * Defaults to 'en' if not supported.
 */
export function detectBrowserLanguage(): Language {
  if (typeof navigator === 'undefined') return 'en';
  const lang = navigator.language?.toLowerCase() ?? '';
  if (lang.startsWith('es')) return 'es';
  return 'en';
}

/**
 * Get translations object for a given language.
 * Returns the full translations object for direct access.
 */
export function getTranslations(language: Language): typeof en {
  return translations[language] ?? translations['en'];
}
